import { Box, Center, Flex, useDisclosure } from "@chakra-ui/react";
import { FormProvider, Resolver, useForm, useFormState } from "react-hook-form";
import { InvoiceFromOcrDto, InvoiceSenderDto } from "@/api/invoiceApi";
import {
    convertInvoiceToForm,
    ocrBankAccountExists,
    filteredBankAccounts,
    useInvoiceEventRedirect
} from "@/utils/invoiceUtils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState, useMemo } from "react";
import { useInvoiceFormChangeTracking } from "./useInvoiceFormChangeTracking.hook";
import { useMutation } from "@tanstack/react-query";
import {
    submitInvoiceByPostMutationOptions,
    updateInvoiceByPutMutationOptions
} from "@/api/queryOptions/invoiceAPIQueryOptions";
import { useBankAccountFieldEffects } from "./useBankAccountFieldEffects.hook";
import { useNavigate, useParams } from "react-router-dom";
import { useSlowRequestToast } from "@/hooks/useSlowRequestToast";
import { getTotalAmount } from "@/utils/costUtils";
import { useSubmit } from "./useSubmit.hook";

import { BankDetailsModal } from "./bankDetailsModal";
import { CurrencyChangeModal } from "./CurrencyChangeModal";
import { NewBankAccountDetectedModal } from "./NewBankAccountDetectedModal";
import { InvoiceFormHeader } from "./header";
import { InvoiceAttachmentSection } from "./invoiceAttachmentSection";
import InvoiceDetailsAndExpenses from "./InvoiceDetailsAndExpenses";
import { InvoiceDetailsDto, ClaimsDetailsDto, InvoiceFormData, BankAccountDto } from "@/types";
import { createInvoiceSchema } from "./schema";
import useSubmission from "./useSubmission.hook";

type InvoiceFormProps = {
    userRole: string;
    invoice: InvoiceDetailsDto;
    ocrResponse?: InvoiceFromOcrDto;
    invoiceSender?: InvoiceSenderDto;
    crmId?: string;
    claim?: ClaimsDetailsDto;
    assignedSubclaimRefs?: string[];
    isLoadingOcr?: boolean;
    isLoadingClaim?: boolean;
};

export const InvoiceForm = ({
    userRole,
    invoice,
    ocrResponse,
    invoiceSender,
    crmId,
    claim,
    assignedSubclaimRefs,
    isLoadingOcr,
    isLoadingClaim
}: InvoiceFormProps) => {
    const invoiceId = useParams<{ invoiceId: string }>().invoiceId || "";
    const navigate = useNavigate();
    const { broadcast } = useInvoiceEventRedirect(navigate);
    const { bankAccounts: bankAccountsData = [] } = invoiceSender || {};

    const formDefault = useMemo(
        () => convertInvoiceToForm(invoice, ocrResponse, claim, bankAccountsData || [], userRole, assignedSubclaimRefs),
        [invoice, ocrResponse, claim, bankAccountsData, userRole, assignedSubclaimRefs]
    );

    const methods = useForm<InvoiceFormData>({
        resolver: zodResolver(createInvoiceSchema(userRole)) as unknown as Resolver<InvoiceFormData>,
        mode: "onBlur",
        defaultValues: formDefault
    });

    const ocrBankAccount = useMemo(
        () => (ocrResponse ? { accountNumber: ocrResponse.bankAccount, currency: ocrResponse.currency } : undefined),
        [ocrResponse]
    );
    const filteredBankAccountsMemo = useMemo(
        () => filteredBankAccounts(bankAccountsData, ocrBankAccount, invoice.bankAccount),
        [bankAccountsData, ocrBankAccount, invoice.bankAccount]
    );

    const [totalAmount, setTotalAmount] = useState(() => getTotalAmount(formDefault));
    const { isFormChanged, formChangeVersionRef, markFormChanged, clearFormChangedIfCurrent, resetFormChanged } =
        useInvoiceFormChangeTracking({ methods, setTotalAmount });
    const [changedCurrency, setChangedCurrency] = useState<boolean>(false);
    const [missingBankAccount, setMissingBankAccount] = useState<string | undefined>();
    const [previousCurrency, setPreviousCurrency] = useState<string | undefined>();
    const [bankAccounts, setBankAccounts] = useState<BankAccountDto[]>(() => filteredBankAccountsMemo);

    const { open: isBankDetailsOpen, onOpen: onOpenBankDetails, onClose: onCloseBankDetails } = useDisclosure();
    const {
        open: isNewBankDetectedOpen,
        onOpen: onOpenNewBankDetected,
        onClose: onCloseNewBankDetected
    } = useDisclosure();

    const ocrBankAccExists = ocrBankAccountExists(bankAccounts, ocrBankAccount);
    const actOnBehalfOf = globalThis.sessionStorage.getItem("actOnBehalfOf");
    const selectedBranch = globalThis.sessionStorage.getItem("selectedBranch");

    const { setValue, getValues, watch, setError, handleSubmit, control, reset, trigger } = methods;

    const clientVatNumber = watch("clientVat");
    const bankAccountWatch = watch("bankAccount");

    // When getting the ocr respons or changing it, reset the form
    useEffect(() => {
        if (!ocrResponse && !claim) {
            return;
        }

        reset(
            {
                ...formDefault
            },
            { keepDefaultValues: false }
        );
        trigger(["invoiceDueDate", "invoiceNumber", "invoiceDate"]);
        resetFormChanged();
    }, [ocrResponse, reset, claim, assignedSubclaimRefs, methods, resetFormChanged]);

    // Always make sure the bankaccounts list is up to date
    useEffect(() => {
        setBankAccounts(filteredBankAccountsMemo);
    }, [filteredBankAccountsMemo]);

    // Open "new bank account detected" modal when OCR bank does not exist and invoice has no selected bank account
    useEffect(() => {
        const hasInvoiceBankNumber = Boolean(invoice.bankAccount?.accountNumber);
        if (ocrBankAccount?.accountNumber && ocrBankAccount?.currency && !ocrBankAccExists && !hasInvoiceBankNumber) {
            onOpenNewBankDetected();
        }
    }, [ocrBankAccExists, onOpenNewBankDetected, ocrBankAccount]);

    useEffect(() => {
        if (actOnBehalfOf) {
            setValue("submittedOnBehalfOfCompanyName", JSON.parse(actOnBehalfOf).companyName);
            setValue("submittedOnBehalfOfCrmId", JSON.parse(actOnBehalfOf).crmId.toString());
        }
        if (selectedBranch) {
            setValue("branchCompanyCrmId", JSON.parse(selectedBranch).crmId.toString());
        }
    }, [actOnBehalfOf, selectedBranch, setValue]);

    const handleCloseCurrencyChangeModal = useCallback(() => {
        setChangedCurrency(false);
        setPreviousCurrency(getValues("bankAccount")?.currency);
        markFormChanged();
    }, [setChangedCurrency, setPreviousCurrency, getValues, markFormChanged]);

    const {
        mutateAsync: updateInvoice,
        isPending: isUpdatingInvoice,
        isError: isUpdateInvoiceError,
        error: updateInvoiceErrorMessage
    } = useMutation(updateInvoiceByPutMutationOptions);

    const {
        mutateAsync: submitInvoice,
        isPending: isSubmittingInvoice,
        isSuccess: isSubmitInvoiceSuccess,
        isError: isSubmitInvoiceError,
        error: submitInvoiceMutationErrorMessage
    } = useMutation(submitInvoiceByPostMutationOptions);

    const isInvoiceInSubmission = isUpdatingInvoice || isSubmittingInvoice;
    const isInvoiceSubmitError = isUpdateInvoiceError || isSubmitInvoiceError;
    const submitInvoiceErrorMessage = updateInvoiceErrorMessage ?? submitInvoiceMutationErrorMessage;

    useSlowRequestToast({
        isLoading: isInvoiceInSubmission
    });

    useBankAccountFieldEffects({
        bankAccountWatch,
        missingBankAccount,
        setMissingBankAccount,
        isFormChanged,
        setChangedCurrency,
        setPreviousCurrency,
        previousCurrency
    });

    const { onSubmit, onErrorSubmit, submitError } = useSubmit({
        totalAmount,
        bankAccounts,
        invoiceId,
        clientVatNumber,
        updateInvoice,
        submitInvoice,
        setMissingBankAccount,
        resetFormChanged,
        setError,
        getValues,
        formState: useFormState<InvoiceFormData>({ control })
    });

    useSubmission({
        isSubmitSuccess: isSubmitInvoiceSuccess,
        isSubmitError: isInvoiceSubmitError,
        submitErrorMessage: submitInvoiceErrorMessage,
        invoiceId,
        broadcast,
        navigate
    });

    return (
        <FormProvider {...methods}>
            <Center>
                <Box
                    m={{ base: "1rem", lg: "1.5rem" }}
                    minW={{ base: "100%", lg: "70rem" }}
                    w="80rem"
                    alignItems="center"
                >
                    <NewBankAccountDetectedModal
                        isOpen={isNewBankDetectedOpen}
                        onClose={onCloseNewBankDetected}
                        onConfirm={onOpenBankDetails}
                        accountDisplay={`${ocrBankAccount?.accountNumber} - ${ocrBankAccount?.currency}`}
                    />
                    <BankDetailsModal
                        invoiceId={invoiceId}
                        isOpen={isBankDetailsOpen}
                        onClose={onCloseBankDetails}
                        defaultBankValues={ocrBankAccExists ? undefined : ocrBankAccount}
                        crmId={crmId}
                        methods={methods}
                        setBankAccounts={setBankAccounts}
                    />
                    <CurrencyChangeModal onClose={handleCloseCurrencyChangeModal} isOpen={changedCurrency} />
                    <InvoiceFormHeader
                        invoiceId={invoiceId}
                        isInvoiceInSubmission={isInvoiceInSubmission}
                        isFormChanged={isFormChanged}
                        clearFormChangedIfCurrent={clearFormChangedIfCurrent}
                        formChangeVersionRef={formChangeVersionRef}
                        bankAccounts={bankAccounts}
                        invoiceStatus={invoice.status}
                    />
                    <Flex w="100%" gap="2rem" flexDir="column">
                        <InvoiceAttachmentSection
                            invoiceId={invoiceId}
                            markFormChanged={markFormChanged}
                            isLoadingOcr={isLoadingOcr}
                            ocrResponse={ocrResponse}
                        />
                        <form onSubmit={handleSubmit(onSubmit, onErrorSubmit)}>
                            <InvoiceDetailsAndExpenses
                                bankAccounts={bankAccounts}
                                invoiceId={invoiceId}
                                missingBankAccount={missingBankAccount}
                                onOpenBankDetails={onOpenBankDetails}
                                totalAmount={totalAmount}
                                invoiceSender={invoiceSender}
                                ocrResponse={ocrResponse}
                                submitError={submitError}
                                crmId={crmId}
                                isSubmittingInvoice={isInvoiceInSubmission}
                                isLoadingOcr={isLoadingOcr}
                                userRole={userRole}
                                isLoadingClaim={isLoadingClaim || (Boolean(claim?.id) && !watch("claimRef"))}
                            />
                        </form>
                    </Flex>
                </Box>
            </Center>
        </FormProvider>
    );
};
